import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  TextField,
  FormControlLabel,
  Checkbox,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Fab
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Clear as ClearIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { collection, query, where, getDocs, addDoc, doc, getDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'

const WordTaskCreate = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  
  // Form state
  const [taskTitle, setTaskTitle] = useState('')
  const [taskDescription, setTaskDescription] = useState('')
  const [words, setWords] = useState([])
  const [selectedStudentIds, setSelectedStudentIds] = useState([])
  const [selectedStudentNames, setSelectedStudentNames] = useState([])
  const [selectedClassId, setSelectedClassId] = useState(null)
  const [selectedClassName, setSelectedClassName] = useState(null)
  const [isIndividualSelection, setIsIndividualSelection] = useState(false)
  
  // Task settings
  const [unlimited, setUnlimited] = useState(true)
  const [dueDate, setDueDate] = useState(null)
  const [taskMode, setTaskMode] = useState('study') // 'study', 'matching', 'meaning'
  
  // Dialog states
  const [showAddWordDialog, setShowAddWordDialog] = useState(false)
  const [showClassDialog, setShowClassDialog] = useState(false)
  const [showStudentDialog, setShowStudentDialog] = useState(false)
  const [showSelectionDialog, setShowSelectionDialog] = useState(false)
  
  // Dialog form states
  const [newWord, setNewWord] = useState({ word: '', meaning: '', pronunciation: '' })
  const [teacherClasses, setTeacherClasses] = useState([])
  const [classStudents, setClassStudents] = useState([])
  const [selectedStudents, setSelectedStudents] = useState([])
  
  // Selection dialog states
  const [tempClassId, setTempClassId] = useState(null)
  const [tempClassName, setTempClassName] = useState(null)
  
  // Saved tasks states
  const [showSavedTasksDialog, setShowSavedTasksDialog] = useState(false)
  const [savedTasks, setSavedTasks] = useState([])
  const [loadingSavedTasks, setLoadingSavedTasks] = useState(false)

  useEffect(() => {
    if (user) {
      // Component ready
    }
  }, [user])

  const handleAddWord = () => {
    if (!newWord.word.trim() || !newWord.meaning.trim()) {
      toast.error('Kelime ve anlamı zorunludur')
      return
    }

    const word = {
      id: Date.now(),
      word: newWord.word.trim(),
      meaning: newWord.meaning.trim(),
      pronunciation: newWord.pronunciation.trim()
    }

    setWords([...words, word])
    setNewWord({ word: '', meaning: '', pronunciation: '' })
    setShowAddWordDialog(false)
    updateCreateButton()
  }

  const handleDeleteWord = (wordId) => {
    setWords(words.filter(w => w.id !== wordId))
    updateCreateButton()
  }

  const handleModeChange = (mode) => {
    setTaskMode(mode)
  }

  const handleUnlimitedChange = (checked) => {
    setUnlimited(checked)
    if (checked) {
      setDueDate(null)
    }
  }

  const loadTeacherClasses = async () => {
    try {
      setLoading(true)
      const classesRef = collection(db, 'classes')
      const q = query(classesRef, where('teacherId', '==', user.uid))
      const querySnapshot = await getDocs(q)
      
      const classes = []
      for (const doc of querySnapshot.docs) {
        const classData = {
          id: doc.id,
          ...doc.data()
        }
        
        // Her sınıf için öğrenci sayısını dinamik olarak hesapla
        try {
          const usersRef = collection(db, 'users')
          const studentQuery = query(
            usersRef,
            where('role', '==', 'student'),
            where('classIds', 'array-contains', doc.id)
          )
          const studentSnapshot = await getDocs(studentQuery)
          classData.studentCount = studentSnapshot.size
        } catch (error) {
          console.error('Error calculating student count for class:', doc.id, error)
          classData.studentCount = 0
        }
        
        classes.push(classData)
      }
      
      setTeacherClasses(classes)
      setShowClassDialog(true)
    } catch (error) {
      console.error('Error loading classes:', error)
      toast.error('Sınıflar yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleClassSelect = (classData) => {
    setTempClassId(classData.id)
    setTempClassName(classData.className)
    setShowClassDialog(false)
    setShowSelectionDialog(true)
  }

  const handleSelectionType = (isIndividual) => {
    setIsIndividualSelection(isIndividual)
    setShowSelectionDialog(false)
    
    if (isIndividual) {
      loadStudentsForIndividualSelection(tempClassId, tempClassName)
    } else {
      loadAllStudentsForClass(tempClassId, tempClassName)
    }
  }

  const loadStudentsForIndividualSelection = async (classId, className) => {
    try {
      setLoading(true)
      const usersRef = collection(db, 'users')
      const q = query(
        usersRef,
        where('role', '==', 'student'),
        where('classIds', 'array-contains', classId)
      )
      const querySnapshot = await getDocs(q)
      
      const students = []
      querySnapshot.forEach((doc) => {
        students.push({
          id: doc.id,
          name: doc.data().username || 'Bilinmeyen Öğrenci'
        })
      })
      
      setClassStudents(students)
      setSelectedStudents([])
      setShowStudentDialog(true)
    } catch (error) {
      console.error('Error loading students:', error)
      toast.error('Öğrenciler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const loadAllStudentsForClass = async (classId, className) => {
    try {
      setLoading(true)
      const usersRef = collection(db, 'users')
      const q = query(
        usersRef,
        where('role', '==', 'student'),
        where('classIds', 'array-contains', classId)
      )
      const querySnapshot = await getDocs(q)
      
      const studentIds = []
      const studentNames = []
      
      querySnapshot.forEach((doc) => {
        studentIds.push(doc.id)
        studentNames.push(doc.data().username || 'Bilinmeyen Öğrenci')
      })
      
      setSelectedStudentIds(studentIds)
      setSelectedStudentNames(studentNames)
      setSelectedClassId(classId)
      setSelectedClassName(className)
      updateCreateButton()
      
      toast.success(`Sınıf seçildi: ${className} (${studentIds.length} öğrenci)`)
    } catch (error) {
      console.error('Error loading all students:', error)
      toast.error('Öğrenciler yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleStudentSelection = (studentId, studentName, isSelected) => {
    if (isSelected) {
      setSelectedStudents([...selectedStudents, { id: studentId, name: studentName }])
    } else {
      setSelectedStudents(selectedStudents.filter(s => s.id !== studentId))
    }
  }

  const handleStudentDialogConfirm = () => {
    const studentIds = selectedStudents.map(s => s.id)
    const studentNames = selectedStudents.map(s => s.name)
    
    setSelectedStudentIds(studentIds)
    setSelectedStudentNames(studentNames)
    setSelectedClassId(tempClassId)
    setSelectedClassName(tempClassName)
    setShowStudentDialog(false)
    updateCreateButton()
  }

  const clearSelectedStudents = () => {
    setSelectedStudentIds([])
    setSelectedStudentNames([])
    setSelectedClassId(null)
    setSelectedClassName(null)
    updateCreateButton()
  }

  const updateCreateButton = () => {
    // Button state will be controlled by the component
  }

  const canCreateTask = () => {
    return taskTitle.trim() && 
           words.length > 0 && 
           selectedStudentIds.length > 0 &&
           (taskMode !== 'matching' || words.length >= 4)
  }

  const createTask = async () => {
    if (!canCreateTask()) {
      if (!taskTitle.trim()) {
        toast.error('Görev başlığı girin')
        return
      }
      if (words.length === 0) {
        toast.error('En az bir kelime ekleyin')
        return
      }
      if (taskMode === 'matching' && words.length < 4) {
        toast.error('Eşleştirme görevi için en az 4 kelime koymalısınız')
        return
      }
      if (selectedStudentIds.length === 0) {
        toast.error('En az bir öğrenci seçin')
        return
      }
      return
    }

    try {
      setSubmitting(true)
      
      const taskData = {
        teacherId: user.uid,
        title: taskTitle.trim(),
        description: taskDescription.trim(),
        createdDate: new Date(),
        studentIds: selectedStudentIds,
        studentNames: selectedStudentNames,
        unlimited: unlimited,
        taskMode: taskMode,
        classId: selectedClassId,
        className: selectedClassName,
        isIndividualSelection: isIndividualSelection,
        words: words.map(w => ({
          word: w.word,
          meaning: w.meaning,
          pronunciation: w.pronunciation
        }))
      }

      if (dueDate && !unlimited) {
        taskData.dueDate = dueDate
      }

      await addDoc(collection(db, 'wordTasks'), taskData)
      
      toast.success('Kelime görevi başarıyla oluşturuldu ve gönderildi')
      onBack()
    } catch (error) {
      console.error('Error creating task:', error)
      toast.error('Görev oluşturulurken hata oluştu')
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (date) => {
    if (!date) return 'Tarih Seç'
    return new Intl.DateTimeFormat('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }).format(date)
  }

  const saveTask = async () => {
    if (!taskTitle.trim()) {
      toast.error('Görev başlığı boş olamaz')
      return
    }

    if (words.length === 0) {
      toast.error('En az bir kelime eklemelisiniz')
      return
    }

    try {
      const taskData = {
        title: taskTitle.trim(),
        description: taskDescription.trim(),
        mode: taskMode,
        words: words.map(w => ({
          word: w.word,
          meaning: w.meaning,
          pronunciation: w.pronunciation
        })),
        dueDate: dueDate,
        unlimited: unlimited,
        createdAt: new Date()
      }

      await addDoc(collection(db, 'teachers', user.uid, 'savedTasks'), taskData)
      toast.success('Görev başarıyla kaydedildi')
    } catch (error) {
      console.error('Error saving task:', error)
      toast.error('Görev kaydedilirken hata oluştu')
    }
  }

  const loadSavedTasks = async () => {
    try {
      setLoadingSavedTasks(true)
      const savedTasksRef = collection(db, 'teachers', user.uid, 'savedTasks')
      const querySnapshot = await getDocs(savedTasksRef)
      
      const tasks = []
      querySnapshot.forEach((doc) => {
        tasks.push({
          id: doc.id,
          ...doc.data()
        })
      })
      
      setSavedTasks(tasks)
      setShowSavedTasksDialog(true)
    } catch (error) {
      console.error('Error loading saved tasks:', error)
      toast.error('Kayıtlı görevler yüklenirken hata oluştu')
    } finally {
      setLoadingSavedTasks(false)
    }
  }

  const loadSavedTaskData = (taskData) => {
    setTaskTitle(taskData.title || '')
    setTaskDescription(taskData.description || '')
    setTaskMode(taskData.mode || 'study')
    setUnlimited(taskData.unlimited !== false)
    setDueDate(taskData.dueDate ? new Date(taskData.dueDate.toDate()) : null)
    
    const loadedWords = taskData.words?.map((word, index) => ({
      id: Date.now() + index,
      word: word.word || '',
      meaning: word.meaning || '',
      pronunciation: word.pronunciation || ''
    })) || []
    
    setWords(loadedWords)
    setShowSavedTasksDialog(false)
    toast.success('Görev yüklendi')
  }

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
        {/* App Bar */}
        <AppBar position="static" sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Kelime Görevi Oluştur
          </Typography>
          <IconButton color="inherit" onClick={saveTask}>
            <SaveIcon />
          </IconButton>
          <IconButton color="inherit" onClick={loadSavedTasks}>
            <DownloadIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Task Info Section */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              Görev Bilgileri
            </Typography>
            
            <Typography variant="body2" color="text.secondary" mb={2}>
              Görev başlığı ve açıklamasını girin
            </Typography>

            <TextField
              fullWidth
              label="Görev Başlığı"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Görev Açıklaması (İsteğe bağlı)"
              value={taskDescription}
              onChange={(e) => setTaskDescription(e.target.value)}
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />

            {/* Due Date Section */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Son Çözüm Tarihi:
              </Typography>
              
              <FormControlLabel
                control={
                  <Checkbox
                    checked={unlimited}
                    onChange={(e) => handleUnlimitedChange(e.target.checked)}
                  />
                }
                label="Sınırsız"
                sx={{ mb: 1 }}
              />
              
              {!unlimited && (
                <TextField
                  type="date"
                  fullWidth
                  value={dueDate ? dueDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => setDueDate(e.target.value ? new Date(e.target.value) : null)}
                  sx={{ mb: 1 }}
                />
              )}
            </Box>

            {/* Task Mode Section */}
            <Typography variant="body2" color="text.secondary" mb={1}>
              Görev Modu Seçin:
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={taskMode === 'study'}
                  onChange={() => handleModeChange('study')}
                />
              }
              label="Çalışma Modu - Öğrenci kelimeleri sadece çalışır"
              sx={{ mb: 1 }}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={taskMode === 'matching'}
                  onChange={() => handleModeChange('matching')}
                />
              }
              label="Eşleştirme Oyunu - Kelime-anlam eşleştirme"
              sx={{ mb: 1 }}
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={taskMode === 'meaning'}
                  onChange={() => handleModeChange('meaning')}
                />
              }
              label="Anlam Sorma - Kelimeleri ver, anlamlarını sor"
            />
          </CardContent>
        </Card>

        {/* Words Section */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              Kelimeler
            </Typography>
            
            <Typography variant="body2" color="text.secondary" mb={2}>
              Öğrenilecek kelimeleri ekleyin
            </Typography>

            <Button
              fullWidth
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddWordDialog(true)}
              sx={{ mb: 2 }}
            >
              Kelime Ekle
            </Button>

            {/* Words List */}
            {words.map((word, index) => (
              <Card key={word.id} sx={{ mb: 2, borderRadius: 1 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      Kelime {index + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteWord(word.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  
                  <Typography variant="h6" fontWeight="bold">
                    {word.word}
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary">
                    {word.meaning}
                  </Typography>
                  
                  {word.pronunciation && (
                    <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                      {word.pronunciation}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </CardContent>
        </Card>

        {/* Student Selection Section */}
        <Card sx={{ mb: 3, borderRadius: 2 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
              Öğrenci Seçimi
            </Typography>
            
            <Typography variant="body2" color="text.secondary" mb={2}>
              Görevi göndereceğiniz öğrencileri seçin
            </Typography>

            {selectedClassName && (
              <Typography variant="body2" color="primary" mb={2}>
                Seçilen Sınıf: {selectedClassName}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={loadTeacherClasses}
              disabled={loading}
              sx={{ mb: 2 }}
            >
              {loading ? <CircularProgress size={20} /> : 'Sınıf Seç'}
            </Button>

            {/* Selected Students */}
            {selectedStudentNames.length > 0 && (
              <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" fontWeight="bold">
                    Seçilen Öğrenciler:
                  </Typography>
                  <IconButton size="small" onClick={clearSelectedStudents}>
                    <ClearIcon />
                  </IconButton>
                </Box>
                
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {selectedStudentNames.map((name, index) => (
                    <Chip
                      key={index}
                      label={name}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Create Task Button */}
        <Button
          fullWidth
          variant="contained"
          size="large"
          onClick={createTask}
          disabled={!canCreateTask() || submitting}
          sx={{ mb: 2 }}
        >
          {submitting ? (
            <CircularProgress size={20} />
          ) : (
            `Görev Oluştur${selectedStudentIds.length > 0 ? ` (${selectedStudentIds.length} öğrenci)` : ''}`
          )}
        </Button>
      </Container>

      {/* Add Word Dialog */}
      <Dialog
        open={showAddWordDialog}
        onClose={() => setShowAddWordDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Kelime Ekle</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Kelime"
            value={newWord.word}
            onChange={(e) => setNewWord({ ...newWord, word: e.target.value })}
            sx={{ mb: 2, mt: 1 }}
          />
          
          <TextField
            fullWidth
            label="Anlamı"
            value={newWord.meaning}
            onChange={(e) => setNewWord({ ...newWord, meaning: e.target.value })}
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Telaffuz (İsteğe bağlı)"
            value={newWord.pronunciation}
            onChange={(e) => setNewWord({ ...newWord, pronunciation: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddWordDialog(false)}>
            İptal
          </Button>
          <Button onClick={handleAddWord} variant="contained">
            Ekle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Class Selection Dialog */}
      <Dialog
        open={showClassDialog}
        onClose={() => setShowClassDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sınıfınızı Seçin</DialogTitle>
        <DialogContent>
          {teacherClasses.length === 0 ? (
            <Alert severity="info">
              Henüz sınıf oluşturmamışsınız
            </Alert>
          ) : (
            <List>
              {teacherClasses.map((classData) => (
                <ListItem
                  key={classData.id}
                  button
                  onClick={() => handleClassSelect(classData)}
                >
                  <ListItemText
                    primary={classData.className}
                    secondary={`${classData.studentCount || 0} öğrenci`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowClassDialog(false)}>
            İptal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Selection Type Dialog */}
      <Dialog
        open={showSelectionDialog}
        onClose={() => setShowSelectionDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Sınıf: {tempClassName}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={2}>
            Öğrenci seçim yöntemini belirleyin:
          </Typography>
          
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleSelectionType(false)}
            sx={{ mb: 2 }}
          >
            Tüm Sınıfı Seç
          </Button>
          
          <Button
            fullWidth
            variant="outlined"
            onClick={() => handleSelectionType(true)}
          >
            Bireysel Seçim Yap
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSelectionDialog(false)}>
            İptal
          </Button>
        </DialogActions>
      </Dialog>

      {/* Student Selection Dialog */}
      <Dialog
        open={showStudentDialog}
        onClose={() => setShowStudentDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Öğrencileri Seçin</DialogTitle>
        <DialogContent>
          <List>
            {classStudents.map((student) => (
              <ListItem key={student.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedStudents.some(s => s.id === student.id)}
                      onChange={(e) => handleStudentSelection(student.id, student.name, e.target.checked)}
                    />
                  }
                  label={student.name}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStudentDialog(false)}>
            İptal
          </Button>
          <Button onClick={handleStudentDialogConfirm} variant="contained">
            Seç
          </Button>
                 </DialogActions>
       </Dialog>

       {/* Saved Tasks Dialog */}
       <Dialog
         open={showSavedTasksDialog}
         onClose={() => setShowSavedTasksDialog(false)}
         maxWidth="md"
         fullWidth
       >
         <DialogTitle>Kayıtlı Görevler</DialogTitle>
         <DialogContent>
           {loadingSavedTasks ? (
             <Box sx={{ textAlign: 'center', py: 4 }}>
               <CircularProgress />
               <Typography sx={{ mt: 2 }}>Yükleniyor...</Typography>
             </Box>
           ) : savedTasks.length === 0 ? (
             <Alert severity="info">
               Henüz kayıtlı görev bulunmuyor
             </Alert>
           ) : (
             <List>
               {savedTasks.map((task) => (
                 <Card key={task.id} sx={{ mb: 2, borderRadius: 1 }}>
                   <CardContent sx={{ p: 2 }}>
                     <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                       <Typography variant="h6" fontWeight="bold">
                         {task.title}
                       </Typography>
                       <Button
                         size="small"
                         variant="contained"
                         onClick={() => loadSavedTaskData(task)}
                       >
                         Yükle
                       </Button>
                     </Box>
                     
                     {task.description && (
                       <Typography variant="body2" color="text.secondary" mb={1}>
                         {task.description}
                       </Typography>
                     )}
                     
                     <Typography variant="body2" color="text.secondary">
                       Kelime Sayısı: {task.words?.length || 0} | 
                       Mod: {task.mode === 'study' ? 'Çalışma' : task.mode === 'matching' ? 'Eşleştirme' : 'Anlam Sorma'} |
                       Tarih: {task.createdAt ? formatDate(task.createdAt.toDate()) : '-'}
                     </Typography>
                   </CardContent>
                 </Card>
               ))}
             </List>
           )}
         </DialogContent>
         <DialogActions>
           <Button onClick={() => setShowSavedTasksDialog(false)}>
             Kapat
           </Button>
         </DialogActions>
       </Dialog>
       </Box>
     </SpaceTheme>
   )
 }

export default WordTaskCreate
